import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { queryKeys } from "@/api/queryKeys";
import {
  realtimeSocket,
  type RealtimeMode,
  type TopicSignal,
} from "@/realtime/socket";
import { useSession } from "@/session/SessionProvider";

export type { RealtimeEvent, RealtimeMode } from "@/realtime/socket";

type EventHandler = Parameters<typeof realtimeSocket.subscribe>[1];

type RealtimeContextValue = {
  mode: RealtimeMode;
  subscribe: (topic: string, handler: EventHandler) => () => void;
};

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

const TOPIC_QUERY_KEYS: Record<string, string[][]> = {
  "world.races": [["races"], ["schedule"], ["home"], ["form-guide"]],
  "world.packs": [["packs"], ["live-opens"], ["player"], ["stable"]],
  "world.market": [["market"], ["player"]],
  "world.standings": [["leaderboards"], ["horses"], ["player"], ["prizes"]],
  "world.jackpot": [["jackpot"], ["prizes"], ["leaderboards"]],
};

const BALANCE_RELEVANT_PRIVATE_EVENTS = new Set([
  "balance.updated",
  "bet.accepted",
  "bet.settled",
  "buyback.executed",
  "cashier.transaction",
  "jackpot.paid",
  "marketplace.settled",
  "pack.delivered",
  "prize.paid",
  "purse.paid",
]);

export function isBalanceRelevantSignal(signal: TopicSignal): boolean {
  if (!signal.topic.startsWith("player.")) return false;
  return "resync" in signal || BALANCE_RELEVANT_PRIVATE_EVENTS.has(signal.type);
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { session } = useSession();
  const [mode, setMode] = useState<RealtimeMode>(realtimeSocket.mode);

  const invalidateTopic = useCallback(
    (signal: TopicSignal) => {
      const { topic } = signal;
      const direct = TOPIC_QUERY_KEYS[topic] ?? [];
      const raceSpecific = topic.startsWith("race.")
        ? [["race"], ["races"], ["schedule"], ["form-guide"]]
        : [];
      const playerSpecific = topic.startsWith("player.")
        ? [["player"], ["stable"], ["bets"], ["awards"]]
        : [];
      const balanceSpecific = isBalanceRelevantSignal(signal)
        ? [queryKeys.balanceRoot]
        : [];
      for (const key of [
        ...direct,
        ...raceSpecific,
        ...playerSpecific,
        ...balanceSpecific,
      ]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
    },
    [queryClient],
  );

  useEffect(() => {
    realtimeSocket.start();
    return realtimeSocket.onMode(setMode);
  }, []);

  useEffect(() => realtimeSocket.onSignal(invalidateTopic), [invalidateTopic]);

  // Reconnect when the session identity changes so private-topic auth
  // rebinds at the new handshake (the socket is a module singleton now).
  const lastPlayerId = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    const playerId = session?.playerId ?? null;
    if (lastPlayerId.current === undefined) {
      lastPlayerId.current = playerId;
      return;
    }
    if (lastPlayerId.current !== playerId) {
      lastPlayerId.current = playerId;
      realtimeSocket.reset();
    }
  }, [session?.playerId]);

  // The private rail is a global authenticated subscription, not a page-level
  // concern. Its events only invalidate REST state; payload balances are never
  // copied into React Query.
  const privatePlayerId = session?.playerId;
  useEffect(() => {
    if (!privatePlayerId) return;
    return realtimeSocket.subscribe(
      `player.${privatePlayerId}`,
      () => undefined,
    );
  }, [privatePlayerId]);

  const subscribe = useCallback(
    (topic: string, handler: EventHandler) =>
      realtimeSocket.subscribe(topic, handler),
    [],
  );

  const value = useMemo(() => ({ mode, subscribe }), [mode, subscribe]);
  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const value = useContext(RealtimeContext);
  if (!value) {
    throw new Error("useRealtime must be used inside RealtimeProvider");
  }
  return value;
}

export function useRealtimeTopic(topic: string | null) {
  const { subscribe } = useRealtime();
  useEffect(() => {
    if (!topic) return;
    return subscribe(topic, () => undefined);
  }, [subscribe, topic]);
}
